import { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
} from "recharts";
import { ShieldCheck, ShieldAlert, UserX, Percent, Info, ListChecks } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { ChartCard } from "@/components/common/ChartCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtPct, humanize, vacancyTone } from "@/lib/format";

const toneChart = { destructive: "hsl(var(--destructive))", warning: "hsl(var(--warning))", accent: "hsl(var(--accent))", success: "hsl(var(--success))" };
const show = (value) => value === null || value === undefined ? "—" : fmt(value);

export default function Leadership() {
    const { data } = useDashboard();
    const lp = data.leadership_profile;
    const filled = lp.total_application_positions - lp.total_vacant_positions;
    const assessments = useMemo(() => lp.district_priority_assessment || [], [lp]);

    const levelData = lp.by_organisation_level.map((l) => ({
        name: humanize(l.level),
        "Vacancy %": l.vacancy_rate_pct,
        tone: vacancyTone(l.vacancy_rate_pct),
    }));

    const highPriority = assessments.filter((d) => d.priority_level === "high").length;
    const incomplete = assessments.filter((d) => d.priority_level === "assessment_incomplete").length;
    const actionDistricts = assessments.filter((d) => d.known_gap_count > 0);

    return (
        <div className="space-y-8">
            <PageHeader
                eyebrow="Leadership"
                title="Positions & vacancies"
                description="Health of the office-bearer structure across all four organisational levels."
            />

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <KpiCard index={0} label="Total Positions" value={fmt(lp.total_application_positions)} sub={`${fmt(filled)} filled`} icon={ShieldCheck} tone="primary" />
                <KpiCard index={1} label="Vacant Positions" value={fmt(lp.total_vacant_positions)} sub="Awaiting appointment" icon={UserX} tone="warning" />
                <KpiCard index={2} label="Overall Vacancy" value={fmtPct(lp.overall_vacancy_rate_pct, 1)} sub="Across the state" icon={Percent} tone="destructive" />
                <KpiCard index={3} label="Critical-Risk Districts" value={fmt(lp.critical_risk_districts)} sub={`${fmt(lp.high_vacancy_districts)} high-vacancy`} icon={ShieldAlert} tone="destructive" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <ChartCard className="lg:col-span-3" title="Vacancy Rate by Level" description="Where leadership gaps concentrate">
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={levelData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="name" {...axisProps} />
                                <YAxis {...axisProps} unit="%" />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmtPct(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                <Bar dataKey="Vacancy %" radius={[6, 6, 0, 0]} maxBarSize={70}>
                                    {levelData.map((d, i) => <Cell key={i} fill={toneChart[d.tone]} />)}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                <Card className="border-border/70 bg-card p-5 shadow-card lg:col-span-2">
                    <h3 className="font-semibold text-foreground">Level Detail</h3>
                    <div className="mt-4 space-y-4">
                        {lp.by_organisation_level.map((l) => {
                            const tone = vacancyTone(l.vacancy_rate_pct);
                            return (
                                <div key={l.level}>
                                    <div className="mb-1.5 flex items-center justify-between">
                                        <span className="text-sm font-medium capitalize text-foreground">{humanize(l.level)}</span>
                                        <StatusBadge tone={tone} label={fmtPct(l.vacancy_rate_pct)} dot={false} />
                                    </div>
                                    <Progress value={l.vacancy_rate_pct} className="h-2" />
                                    <p className="mt-1 text-xs text-muted-foreground">
                                        {fmt(l.vacant_positions)} vacant of {fmt(l.total_positions)} positions
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            </div>

            <div>
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h2 className="font-display text-xl font-semibold text-foreground">Priority Districts Requiring Attention</h2>
                        <p className="mt-1 text-sm text-muted-foreground">District-level organisational health using the approved minimum standards.</p>
                    </div>
                    <Dialog>
                        <DialogTrigger asChild>
                            <Button variant="outline" size="sm"><Info className="h-4 w-4" /> How is Priority Calculated?</Button>
                        </DialogTrigger>
                        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle>Priority Assessment Framework</DialogTitle>
                                <DialogDescription>District priority is assessed using leadership, organisational strength, outreach and Bal Vikas continuity.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4 text-sm">
                                <div className="overflow-x-auto rounded-lg border border-border/70">
                                    <Table>
                                        <TableHeader><TableRow><TableCell className="font-semibold">Indicator</TableCell><TableCell className="font-semibold">Expected standard</TableCell></TableRow></TableHeader>
                                        <TableBody>
                                            <TableRow><TableCell>Critical leadership</TableCell><TableCell>All critical district positions filled</TableCell></TableRow>
                                            <TableRow><TableCell>Samithi presence</TableCell><TableCell>At least 5 Samithis; Bhajan Mandalis excluded</TableCell></TableRow>
                                            <TableRow><TableCell>Adopted villages</TableCell><TableCell>At least 1 for every 3 units</TableCell></TableRow>
                                            <TableRow><TableCell>Vidya Jyothi schools</TableCell><TableCell>At least 1 for every 3 units</TableCell></TableRow>
                                            <TableRow><TableCell>Bal Vikas Gurus</TableCell><TableCell>At least 1 Guru per Samithi or Bhajan Mandali</TableCell></TableRow>
                                            <TableRow><TableCell>Weekly classes</TableCell><TableCell>At least 1 weekly Bal Vikas class in every unit</TableCell></TableRow>
                                        </TableBody>
                                    </Table>
                                </div>
                                <Card className="bg-secondary/40 p-4">
                                    <p className="font-medium text-foreground">Coverage formula</p>
                                    <p className="mt-1 text-muted-foreground">Required villages and schools = ceiling of (Samithis + Bhajan Mandalis) ÷ 3.</p>
                                </Card>
                                <p className="leading-relaxed text-muted-foreground">Where source data is unavailable, the dashboard displays “—” and marks the assessment incomplete. Missing data is never treated as satisfactory performance.</p>
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <Card className="border-border/70 bg-card p-4 shadow-card"><p className="text-xs uppercase tracking-wide text-muted-foreground">Known High Priority</p><p className="mt-1 text-2xl font-semibold tabular text-foreground">{fmt(highPriority)}</p></Card>
                    <Card className="border-border/70 bg-card p-4 shadow-card"><p className="text-xs uppercase tracking-wide text-muted-foreground">Assessment Incomplete</p><p className="mt-1 text-2xl font-semibold tabular text-foreground">{fmt(incomplete)}</p></Card>
                    <Card className="border-border/70 bg-card p-4 shadow-card"><p className="text-xs uppercase tracking-wide text-muted-foreground">Districts with Known Gaps</p><p className="mt-1 text-2xl font-semibold tabular text-foreground">{fmt(actionDistricts.length)}</p></Card>
                </div>

                <Card className="overflow-hidden border-border/70 bg-card shadow-card">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="hover:bg-transparent">
                                    <TableCell className="font-semibold">District</TableCell>
                                    <TableCell className="text-right font-semibold">Samithis</TableCell>
                                    <TableCell className="text-right font-semibold">Bhajan Mandalis</TableCell>
                                    <TableCell className="text-right font-semibold">Samithi Gap</TableCell>
                                    <TableCell className="text-right font-semibold">Village Gap</TableCell>
                                    <TableCell className="text-right font-semibold">VJ Gap</TableCell>
                                    <TableCell className="text-right font-semibold">Guru Gap</TableCell>
                                    <TableCell className="text-right font-semibold">Weekly Class Gap</TableCell>
                                    <TableCell className="font-semibold">Critical Positions</TableCell>
                                    <TableCell className="font-semibold">Priority</TableCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {assessments.map((d) => (
                                    <TableRow key={d.district_id} className="border-border/50">
                                        <TableCell className="font-medium text-foreground">{d.district_name}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(d.samithis)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(d.bhajan_mandalis)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(d.samithi_gap)}</TableCell>
                                        <TableCell className="text-right tabular">{show(d.adopted_village_gap)}</TableCell>
                                        <TableCell className="text-right tabular">{show(d.vidya_jyothi_gap)}</TableCell>
                                        <TableCell className="text-right tabular">{fmt(d.guru_gap)}</TableCell>
                                        <TableCell className="text-right tabular">{show(d.weekly_class_gap)}</TableCell>
                                        <TableCell>{d.critical_position_vacancies === null ? "—" : fmt(d.critical_position_vacancies)}</TableCell>
                                        <TableCell><StatusBadge tone={d.priority_level === "high" ? "destructive" : "muted"} label={d.priority_level === "high" ? "High" : "Incomplete"} /></TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            </div>

            <Card className="border-border/70 bg-gradient-subtle p-5 shadow-card">
                <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-warning/15 text-warning"><ListChecks className="h-6 w-6" /></span>
                    <div>
                        <h3 className="font-semibold text-foreground">Recommended actions</h3>
                        <p className="mt-1 max-w-4xl text-sm leading-relaxed text-muted-foreground">
                            {actionDistricts.length > 0
                                ? `${fmt(actionDistricts.filter((d) => d.samithi_gap > 0).length)} districts need additional Samithi strengthening and ${fmt(actionDistricts.filter((d) => d.guru_gap > 0).length)} districts show a Bal Vikas Guru gap. District-wise outreach, weekly-class continuity and critical-position details should be added to complete the assessment.`
                                : "No known structural or Guru gaps are visible in the available data. District-wise outreach, weekly-class continuity and critical-position details are still required for a complete assessment."}
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
}
